import dayjs from 'dayjs';
import pickBy from 'lodash/pickBy';
import { stringify } from 'query-string';

import { defaultLocale, TCurrency } from '@/config/locales';
import { ECampsiteType } from '@/constants/amenitiesFilter';
import { ESearchFilters } from '@/constants/searchFilters';
import { RV_CAMPSITE_BUNDLES_LANDING_PAGE } from '@/constants/urls';
import { IHeaderSearchFilterDates } from '@/redux/modules/globalHeader';
import { ISearchFormFilters } from '@/redux/modules/searchForm';
import { IFavorite } from '@/redux/modules/wishlists';
import { TRootState } from '@/redux/rootReducer';
import { getUserFavorites } from '@/redux/selectors/favorites';
import { PriceType } from '@/redux/selectors/listing/page';
import { EListingSource } from '@/services/analytics/listings/types';
import { IBundleCampground } from '@/services/types/bundles/id';
import { ICampgroundData } from '@/services/types/search/campgrounds/id';
import {
  ECampsiteCategoryType,
  ERentalCategory,
  ICatalog,
  IData,
  IListingData,
  ITag,
  TEstimatedPriceBreakdown,
} from '@/services/types/search/rentals/id';
import { getIntl } from '@/utility/i18n';
import { formatMeasurement } from '@/utility/measurements';
import { mapPluralUnitToSingular } from '@/utility/units';

import { getOptimisticFavoriteCount } from './getOptimisticFavoriteCount';
import { getCampsiteTypeDisplay, getStayTypeDisplay, getVehicleTypeDisplay } from './rentals';

const IMAGES_LIMIT = 7;

export interface IPrice {
  currency?: TCurrency;
  discount: number;
  instantBook: boolean;
  originalPrice: number;
  currentPrice: number;
  vacationPackageNightlyRate?: number;
  overrideDayPrice?: number;
  discountFromOverrideDayPrice?: number;
  period: PriceType;
  weeklyDiscountPercentage?: number;
  monthlyDiscountPercentage?: number;
  estimatedPriceBreakdown?: TEstimatedPriceBreakdown;
}

export interface IRentalDetails {
  classText?: string;
  feet?: string;
  sleeps?: string;
  beds?: string;
  maxVehicleLength?: string;
  seats?: string;
  infos?: string[];
  location?: string;
  city?: string;
  ratingNumber?: number;
  ratingQtd?: number;
  year?: string;
  make?: string;
  model?: string;
  tags?: ITag[];
  distance?: number;
  distanceUnit?: string;
}
export interface IRentalTile {
  campground?: false;
  currency?: TCurrency;
  dealer?: boolean;
  details?: IRentalDetails;
  id?: number;
  instantBook?: boolean;
  wasBookedBefore?: boolean;
  isFavorite?: boolean;
  isStay?: boolean;
  isCampsite?: boolean;
  campsiteCategoryType?: string;
  linkUrl?: string;
  loading?: boolean;
  photoUrl?: string | string[];
  photoAlt?: string;
  title?: string;
  unavailable?: boolean;
  price?: IPrice;
  blendLogicSource?: string | null;
  displaySiteCategoryType?: string;
  siteCategoryType?: string;
  favoriteCount?: number;
  sleepingLabel?: string;
  catalog?: ICatalog;
  slug?: string;
  viewed?: string;
  viewedUpdated?: string;
  sleeps?: number;
  lat?: number;
  lng?: number;
  ownerId?: number;
  wishlistID?: number;
  wishlistName?: string;
  wishlistCreatorID?: number | null;
  vehicleTitle?: string;
}
export interface ICampgroundPrice {
  currency?: TCurrency;
  lowestPrice: number;
}

interface ICampgroundTileDetails {
  ratingNumber?: number;
  ratingQtd?: number;
  location?: string;
}

export interface ICampgroundTile {
  campground: true;
  dealer?: boolean;
  wasBookedBefore?: boolean;
  isFavorite?: boolean;
  details?: ICampgroundTileDetails;
  currency?: TCurrency;
  description?: string;
  id?: number;
  linkUrl?: string;
  loading?: boolean;
  sitesDisplayText?: string;
  photoUrl?: string | string[];
  photoAlt?: string | undefined;
  photoMetadata?: Array<{
    url: string;
    category?: string;
    tags?: string;
  }>;
  title?: string;
  price?: ICampgroundPrice;
  favoriteCount?: number;
  escapeLinkUrl?: string;
  escapeLocation?: string;
}

export type ISearchResultTile = ICampgroundTile | IRentalTile;

export const isCampgroundTile = (tile: ISearchResultTile): tile is ICampgroundTile => {
  return 'campground' in tile && !!tile.campground;
};

const mapCampgroundToPrice = (campground: ICampgroundData): ICampgroundPrice => {
  const lowestPrice = campground.vacation_package_min_nightly_rate || campground.lowest_price;
  const currency = campground.presentment_currency || defaultLocale.base_currency;
  return {
    currency,
    lowestPrice,
  };
};

export const mapCampgroundTileToRentalPrice = (campground: ICampgroundTile): IPrice => {
  return {
    currency: campground.currency,
    discount: 0,
    instantBook: false,
    currentPrice: campground.price?.lowestPrice || 0,
    originalPrice: campground.price?.lowestPrice || 0,
    period: PriceType.NIGHT,
  };
};

const getCampgroundSitesDisplayText = (campground: ICampgroundData): string => {
  const counts = campground.total_category_counts;
  if (!counts) return '';
  const intl = getIntl();
  const rvCount = counts[ECampsiteType.RV_SITE];
  const tentCount = counts[ECampsiteType.TENT_SITE];
  const lodgingCount = counts[ECampsiteType.LODGING_SITE];
  const countsText = [];
  if (rvCount > 0) {
    countsText.push(
      intl.formatMessage(
        { defaultMessage: '{rvCount, plural, one {# RV site} other {# RV sites}}', id: 'Izh6UW' },
        { rvCount },
      ),
    );
  }
  if (tentCount > 0) {
    countsText.push(
      intl.formatMessage(
        {
          defaultMessage: '{tentCount, plural, one {# Tent site} other {# Tent sites}}',
          id: 'jZtxKc',
        },
        { tentCount },
      ),
    );
  }
  if (lodgingCount > 0) {
    countsText.push(
      intl.formatMessage(
        { defaultMessage: '{lodgingCount} Lodging', id: 'PD+Xao' },
        { lodgingCount },
      ),
    );
  }

  return countsText.join(', ');
};

export const getCampgroundLocationText = (
  campground: ICampgroundData,
  isVacationPackage = false,
  rawLocation = false,
): string => {
  const intl = getIntl();

  if (!campground.location) {
    return '';
  }

  const location = `${campground.location?.city}${
    campground.location?.state && `, ${campground.location?.state}`
  }`;

  if (rawLocation) {
    return location;
  }

  if (isVacationPackage) {
    return intl.formatMessage(
      { defaultMessage: 'RV Resort in {location}', id: 'MygHf+' },
      { location },
    );
  }

  return intl.formatMessage(
    { defaultMessage: 'Campground in {location}', id: 'rltDTf' },
    { location },
  );
};

type TMapCampgroundToTileInput = {
  campground: ICampgroundData;
  loading?: boolean;
  hasImageCarousel?: boolean;
  queryParams?: TRootState['queryParams'];
  searchMeta?: TRootState['search']['meta'];
};
export const mapCampgroundToTile = ({
  campground,
  loading,
  hasImageCarousel,
  queryParams,
}: TMapCampgroundToTileInput): ICampgroundTile => {
  const images = campground.images ? campground.images.map(image => image.url) : [];
  const imagesLimit = 7;
  const primaryImage = campground.primary_image_url || images[0];

  const queriesToCarryOver = [
    ESearchFilters.ADDRESS,
    ESearchFilters.DATE_FROM,
    ESearchFilters.DATE_TO,
    ESearchFilters.FLEXIBLE_DAYS,
    ESearchFilters.GUESTS_ADULTS,
    ESearchFilters.GUESTS_CHILDREN,
    ESearchFilters.GUESTS_INFANTS,
    ESearchFilters.FILTER_FEATURE,
    ESearchFilters.CAMPGROUND_FEATURES,
    ESearchFilters.CAMPSITE_RV_SITE_FEATURES,
    ESearchFilters.CAMPSITE_TENT_SITE_FEATURES,
    ESearchFilters.CAMPSITE_LODGING_FEATURES,
    ESearchFilters.CAMPSITE_MAX_VEHICLE_LENGTH,
    ESearchFilters.CAMPSITE_SUPPORTED_SUBTYPES,
    ESearchFilters.CAMPSITE_SUPPORTED_LENGTH,
    ESearchFilters.CAMPSITE_SUPPORTED_SLIDEOUTS,
    ESearchFilters.CAMPSITE_BATHROOMS,
    ESearchFilters.CAMPSITE_BEDROOMS,
    ESearchFilters.CAMPSITE_BEDS,
    ESearchFilters.CAMPSITE_CATEGORY_TYPES,
    ESearchFilters.PRICE_MIN,
    ESearchFilters.PRICE_MAX,
    'discount_code',
  ] as string[];
  const carryOverQuery =
    queryParams &&
    Object.keys(queryParams)
      .filter(key => queriesToCarryOver.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: queryParams[key] }), {});
  const query: any = queryParams
    ? pickBy({
        ...carryOverQuery,
        from: queryParams[ESearchFilters.DATE_FROM],
        to: queryParams[ESearchFilters.DATE_TO],
      })
    : undefined;

  const linkUrl = `${campground.slug}${query ? `?${stringify(query)}` : ''}`;
  const vacationPackageSlug = campground.vacation_package_slug;

  const escapeLinkUrl = `${RV_CAMPSITE_BUNDLES_LANDING_PAGE}/${vacationPackageSlug?.split('/').pop()}${query ? `?${stringify(query)}` : ''}`;

  return {
    campground: true,
    currency: campground.presentment_currency,
    id: campground.id,
    linkUrl,
    photoUrl: hasImageCarousel && images.length > 1 ? images.slice(0, imagesLimit) : primaryImage,
    title: campground.name,
    loading,
    details: {
      ratingNumber: campground.score,
      ratingQtd: campground.reviews_num,
      location: getCampgroundLocationText(campground),
    },
    sitesDisplayText: getCampgroundSitesDisplayText(campground),
    price: mapCampgroundToPrice(campground),
    escapeLinkUrl,
    escapeLocation: getCampgroundLocationText(campground, false, true),
  };
};

type TMapCampgroundsToTilesInput = {
  campgrounds: ICampgroundData[];
  loading?: boolean;
  hasImageCarousel?: boolean;
  queryParams?: TRootState['queryParams'];
  searchMeta?: TRootState['search']['meta'];
  searchForm?: TRootState['searchForm'];
};
export const mapCampgroundsToTiles = ({
  campgrounds,
  loading,
  hasImageCarousel,
  queryParams,
  searchMeta,
}: TMapCampgroundsToTilesInput): ICampgroundTile[] =>
  campgrounds?.map(campground =>
    mapCampgroundToTile({
      campground,
      loading,
      hasImageCarousel,
      queryParams,
      searchMeta,
    }),
  ) || [];

export interface IBundleTile {
  id: number;
  name: string;
  location: string;
  lowestPrice: number;
  presentmentCurrency: TCurrency;
  slug: string;
  imageUrls: string[];
  isFavorite?: boolean;
}

type TMapBundleToTileInput = {
  bundle: IBundleCampground;
  favoritedEscapes?: IFavorite[];
};

export const mapBundleToTile = ({
  bundle,
  favoritedEscapes,
}: TMapBundleToTileInput): IBundleTile => {
  const location =
    bundle.address?.city && bundle.address?.state
      ? `${bundle.address.city}, ${bundle.address.state}`
      : '';

  return {
    id: bundle.id,
    name: bundle.name,
    location,
    lowestPrice: bundle.min_bundle_base_price,
    presentmentCurrency: 'USD',
    slug:
      bundle.vacation_package_slug?.replace(
        '/vacation-packages/',
        `${RV_CAMPSITE_BUNDLES_LANDING_PAGE}/`,
      ) || '',
    imageUrls: bundle.image_urls,
    isFavorite: favoritedEscapes?.find((f: IFavorite) => f?.item_id === bundle.id) ? true : false,
  };
};

type TMapBundlesToTilesInput = {
  bundles: IBundleCampground[];
  favorites: ReturnType<typeof getUserFavorites>;
};

export const mapBundlesToTiles = ({ bundles, favorites }: TMapBundlesToTilesInput): IBundleTile[] =>
  bundles?.map(bundle =>
    mapBundleToTile({
      bundle,
      favoritedEscapes: favorites.escapes,
    }),
  ) || [];

export const mapRentalToPrice = ({ rental }: { rental?: IData | IListingData | null }) => {
  if (!rental) return undefined;

  const currency = rental.presentment_currency || defaultLocale.base_currency;
  const period = rental?.active_options?.use_day_pricing ? PriceType.DAY : PriceType.NIGHT;
  const currentPrice =
    rental?.active_options?.day_price || rental?.price_per_day || rental.active_price?.day || 0;
  const originalPrice = mapRentalToOriginalPrice(rental);
  const discount =
    Math.round(
      ((originalPrice && currentPrice && 1 - currentPrice / originalPrice) || 0 + Number.EPSILON) *
        100,
    ) || 0;

  return {
    currency,
    discount,
    instantBook: rental.active_options?.instant_book ?? rental.instant_book,
    currentPrice,
    originalPrice,
    vacationPackageNightlyRate: rental.vacation_package_nightly_rate,
    period,
    weeklyDiscountPercentage: rental.active_options?.weekly_discount_percentage,
    monthlyDiscountPercentage: rental.active_options?.monthly_discount_percentage,
    estimatedPriceBreakdown: rental.active_options?.estimated_price_breakdown,
  };
};

export const mapRentalToOriginalPrice = (rental: IData | IListingData | null) => {
  return rental?.price_per_day || rental?.active_price?.day || 0;
};

type TMapRental = {
  index: number;
  rental: IData | IListingData;
  favorites: ReturnType<typeof getUserFavorites>;
  loading?: boolean;
  hasImageCarousel?: boolean;
  queryParams?: TRootState['queryParams'];
  searchMeta?: TRootState['search']['meta'];
  searchForm?: TRootState['searchForm'];
  listingSource?: EListingSource;
  storeDates?: IHeaderSearchFilterDates;
  additionalData?: (rental: IData | IListingData) => {
    viewed?: string;
    viewedUpdated?: string;
  };
};
export const mapRentalToTile = ({
  index,
  rental,
  favorites,
  loading,
  hasImageCarousel,
  queryParams,
  searchForm,
  listingSource,
  storeDates,
  additionalData,
}: TMapRental): IRentalTile => {
  const intl = getIntl();

  const isCampsite = rental.rental_category === ERentalCategory.CAMPGROUND;
  // Treat campsite tiles as stay tiles for most of the logic
  const isStay = rental.rental_category === ERentalCategory.STAY || isCampsite;

  const { beds_king, beds_queen, beds_twin, beds_full } = rental.features || {};

  // Check if all bed counts are 0
  const allBedsZero = beds_full === 0 && beds_king === 0 && beds_queen === 0 && beds_twin === 0;
  let sleepingLabel = '';
  if (allBedsZero) {
    sleepingLabel = intl.formatMessage({ id: 'cWbda3', defaultMessage: 'RV Suite' }); // TODO: update this'
  }

  const bedDescriptions = [];
  if (beds_king) {
    bedDescriptions.push(
      intl.formatMessage(
        { id: 'IDPhev', defaultMessage: '{count, plural, one {# King} other {# Kings}}' },
        { count: beds_king },
      ),
    );
  }
  if (beds_queen) {
    bedDescriptions.push(
      intl.formatMessage(
        { id: 'HKSnTz', defaultMessage: '{count, plural, one {# Queen} other {# Queens}}' },
        { count: beds_queen },
      ),
    );
  }
  if (beds_twin) {
    bedDescriptions.push(
      intl.formatMessage(
        { id: '6hJIWn', defaultMessage: '{count, plural, one {# Twin} other {# Twins}}' },
        { count: beds_twin },
      ),
    );
  }
  if (beds_full) {
    bedDescriptions.push(
      intl.formatMessage(
        { id: 'qch1Lf', defaultMessage: '{count, plural, one {# Full} other {# Fulls}}' },
        { count: beds_full },
      ),
    );
  }

  const rentalSleeps =
    rental.campsite_category?.max_occupancy || rental.stay?.sleeps || rental.sleeps;
  const sleeps = rentalSleeps
    ? intl.formatMessage(
        { defaultMessage: 'Sleeps {sleeps}', id: 'xJdtVO' },
        { sleeps: rentalSleeps },
      )
    : undefined;

  const rentalBeds = rental.campsite_category?.beds || rental.stay?.beds;
  const beds = rentalBeds
    ? intl.formatMessage(
        { defaultMessage: '{total, plural, one {# Bed} other {# Beds}}', id: '5S3L0y' },
        { total: rentalBeds },
      )
    : undefined;

  const year = rental.vehicle ? rental.vehicle?.year : rental.vehicle_year;

  const seats = rental.seatbelts
    ? intl.formatMessage(
        { defaultMessage: 'Seats {seats}', id: 'D+3uGl' },
        { seats: rental.seatbelts },
      )
    : undefined;
  const lengthUnit = rental.locale?.length_unit
    ? mapPluralUnitToSingular(rental.locale.length_unit)
    : 'foot'; // Default to 'foot' when locale or length_unit is missing

  const vehicleLength = rental.vehicle?.length ? rental.vehicle?.length : rental.vehicle_length;

  const feet =
    isStay || !lengthUnit
      ? ''
      : formatMeasurement(vehicleLength, {
          unit: lengthUnit,
        });

  const maxVehicleLength =
    rental.rental_category === ERentalCategory.CAMPGROUND &&
    rental.campsite_category?.category_type === ECampsiteCategoryType.RV_SITE &&
    rental.campsite_category?.max_vehicle_length
      ? intl.formatMessage(
          { defaultMessage: 'RVs up to {length}{lengthUnit}', id: 'saHenM' },
          {
            length: rental.campsite_category.max_vehicle_length,
            lengthUnit: 'ft', // TODO: backend to allow for Locale and meters
          },
        )
      : undefined;

  const foundFavorite =
    favorites && favorites.rentals.find((favorite: IFavorite) => favorite.item_id === rental.id);
  const isFavorite = !!foundFavorite;

  const imageUrls = rental.images ? rental.images.map(image => image.url) : [];

  const primaryImage = rental.primary_image_url || imageUrls[0] || '';

  const queriesToCarryOver = [
    ESearchFilters.ADDRESS,
    ESearchFilters.DATE_FROM,
    ESearchFilters.DATE_TO,
    ESearchFilters.FLEXIBLE_DAYS,
    ESearchFilters.GUESTS_ADULTS,
    ESearchFilters.GUESTS_CHILDREN,
    ESearchFilters.GUESTS_INFANTS,
    ESearchFilters.FILTER_FEATURE,
    ESearchFilters.FILTER_TYPE,
    ESearchFilters.FILTER_RENTER_AGE,
    ESearchFilters.PRICE_MIN,
    ESearchFilters.PRICE_MAX,
    'discount_code',
  ] as string[];
  const carryOverQuery =
    queryParams &&
    Object.keys(queryParams)
      .filter(key => queriesToCarryOver.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: queryParams[key] }), {});

  const hasNoDateParams =
    !queryParams?.[ESearchFilters.DATE_FROM] && !queryParams?.[ESearchFilters.DATE_TO];

  const searchResultOrder = { [ESearchFilters.RESULT_NUMBER]: index };

  const renterAge = queryParams?.[ESearchFilters.FILTER_RENTER_AGE];

  const query: any = queryParams
    ? pickBy({
        ...carryOverQuery,
        ...searchResultOrder,
        from:
          queryParams[ESearchFilters.DATE_FROM] ||
          (hasNoDateParams && storeDates?.from
            ? dayjs(storeDates.from).format('YYYY-MM-DD')
            : undefined),
        to:
          queryParams[ESearchFilters.DATE_TO] ||
          (hasNoDateParams && storeDates?.to
            ? dayjs(storeDates.to).format('YYYY-MM-DD')
            : undefined),
        ...(listingSource && { listingSource }),
        ...(renterAge && { renter_age: renterAge }),
      })
    : searchResultOrder;

  [
    ESearchFilters.DELIVERY,
    ESearchFilters.DELIVERY_STATIONARY,
    ESearchFilters.DELIVERY_CENTER,
    ESearchFilters.MAPBOX_ID,
    ESearchFilters.DELIVERY_LOCATION_ID,
    ESearchFilters.DELIVERY_QUERY,
    ESearchFilters.DELIVERY_DETAILS,
    ESearchFilters.DELIVERY_ADDRESS,
    ESearchFilters.SEARCH_DELIVERY_CAMPGROUND_ID,
    ESearchFilters.CONSTANT_ADDRESS,
  ].forEach((queryParam: any) => {
    if (
      (searchForm && searchForm.filters[queryParam as keyof ISearchFormFilters]) ||
      (queryParams && queryParams[queryParam])
    ) {
      query[queryParam] =
        (searchForm && searchForm.filters[queryParam as keyof ISearchFormFilters]) ||
        (queryParams && queryParams[queryParam]);
    }
  });

  const linkUrl = `${rental.slug}${query ? `?${stringify(query)}` : ''}`;

  const location = rental.location
    ? `${rental.location?.city}${rental.location?.state && `, ${rental.location?.state}`}`
    : `${rental?.home?.city}${rental?.home?.state && `, ${rental?.home?.state}`}`;

  const city = rental.location?.city || rental?.home?.city;

  const classText = (() => {
    if (isCampsite) return getCampsiteTypeDisplay(rental.campsite_category?.display_category_type);
    if (isStay) return getStayTypeDisplay(rental.stay?.display_stay_type);
    return getVehicleTypeDisplay(rental.display_vehicle_type);
  })();

  const photoAlt =
    isCampsite || isStay
      ? undefined
      : `${year} ${rental?.vehicle_make} ${rental?.vehicle_model} ${classText} rental in ${location}`;

  return {
    currency: rental.presentment_currency,
    id: rental.id,
    instantBook: rental.active_options?.instant_book ?? rental.instant_book,
    unavailable: rental.unavailable,
    favoriteCount: getOptimisticFavoriteCount(rental, isFavorite),
    isFavorite: isFavorite,
    isStay,
    isCampsite,
    campsiteCategoryType: isCampsite ? rental.campsite_category?.category_type : undefined,
    linkUrl,
    photoUrl:
      hasImageCarousel && imageUrls.length > 1 ? imageUrls.slice(0, IMAGES_LIMIT) : primaryImage,
    photoAlt,
    title: rental.name,
    loading,
    dealer: rental.dealer,
    sleepingLabel: bedDescriptions.length > 0 ? bedDescriptions.join(', ') : sleepingLabel,
    details: {
      ratingNumber: rental.score,
      ratingQtd: rental.reviews_num ?? rental.score_count,
      location:
        rental.rental_category === ERentalCategory.CAMPGROUND
          ? intl.formatMessage(
              { defaultMessage: 'Campground in {location}', id: 'rltDTf' },
              { location },
            )
          : location,
      city,
      classText,
      feet,
      sleeps,
      beds,
      maxVehicleLength,
      seats,
      infos: [sleeps, seats].filter(attr => Boolean(attr)) as string[],
      tags: rental.tags,
      distance: rental.distance,
      distanceUnit: rental.locale?.distance_unit,
      year: `${year}`,
      make: rental.vehicle_make,
      model: rental.vehicle_model,
    },
    price: mapRentalToPrice({ rental }),
    blendLogicSource: rental.blend_logic_source || null,
    displaySiteCategoryType: rental.campsite_category?.display_category_type,
    siteCategoryType: rental.campsite_category?.category_type,
    catalog: rental.catalog,
    slug: rental.slug,
    sleeps: rental.sleeps,
    lat: rental.location?.lat,
    lng: rental.location?.lng,
    ownerId: rental.owner_user_id,
    vehicleTitle: rental.vehicle_title,
    ...additionalData?.(rental),
    ...(foundFavorite && {
      wishlistID: foundFavorite.wishlist_id,
      wishlistName: foundFavorite.wishlist_name,
      wishlistCreatorID: foundFavorite.wishlist_creator_id,
    }),
  };
};

type TMapRentals = {
  rentals: IData[] | IListingData[];
  favorites: ReturnType<typeof getUserFavorites>;
  loading?: boolean;
  hasImageCarousel?: boolean;
  queryParams?: TRootState['queryParams'];
  searchMeta?: TRootState['search']['meta'];
  searchForm?: TRootState['searchForm'];
  listingSource?: EListingSource;
  storeDates?: IHeaderSearchFilterDates;
  additionalData?: (rental: IData | IListingData) => {
    viewed?: string;
    viewedUpdated?: string;
  };
};

export const mapRentalsToTiles = ({
  rentals,
  favorites,
  loading,
  hasImageCarousel,
  queryParams,
  searchMeta,
  searchForm,
  listingSource,
  storeDates,
  additionalData,
}: TMapRentals): IRentalTile[] =>
  rentals?.map((rental, index) =>
    mapRentalToTile({
      index,
      rental,
      favorites,
      loading,
      hasImageCarousel,
      queryParams,
      searchMeta,
      searchForm,
      listingSource,
      storeDates,
      additionalData,
    }),
  ) || [];
